const froalaLicenseKey = process.env.REACT_APP_FROALA_LICENSE || '';
const defaultWirisServiceProviderUri =
  process.env.REACT_APP_WIRIS_SERVICE_PROVIDER_URI || 'https://www.wiris.net/demo/plugins/app';

const normalizeUrl = (url = '') => url.replace(/\/$/, '');

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const shouldUseProxy = () => {
  const value = `${process.env.REACT_APP_API_USE_PROXY || ''}`.toLowerCase();
  return value === 'true' || value === '1';
};

const buildUploadUrl = () => {
  if (process.env.REACT_APP_FROALA_IMAGE_UPLOAD_URL) {
    return normalizeUrl(process.env.REACT_APP_FROALA_IMAGE_UPLOAD_URL);
  }

  const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_ENDPOINT;
  if (baseUrl) {
    return `${normalizeUrl(baseUrl)}/admin/uploads/image`;
  }

  if (shouldUseProxy()) {
    return '/api/v1/admin/uploads/image';
  }

  return '';
};

const defaultUploadUrl = buildUploadUrl();
const defaultImageMaxSize = toPositiveInt(process.env.REACT_APP_FROALA_IMAGE_MAX_SIZE, 5 * 1024 * 1024);

/**
 * Inject the Froala license key and default image upload behaviour into the
 * provided configuration. Existing config values win unless missing.
 */
export const froalaConfig = {
  toolbarButtons: [
    'bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript', '|',
    'fontFamily', 'fontSize', 'textColor', 'backgroundColor', '|',
    'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'formatOL', 'formatUL', 'paragraphFormat', 'lineHeight', '|',
    'insertLink', 'insertImage', 'insertVideo', 'insertTable', 'insertFile', 'emoticons', 'specialCharacters', '|',
    'wirisEditor', 'wirisChemistry', '|',
    'undo', 'redo', 'fullscreen', 'html'
  ],
  pluginsEnabled: [
    'align', 'charCounter', 'codeBeautifier', 'codeView', 'colors', 'draggable',
    'emoticons', 'entities', 'file', 'fontFamily', 'fontSize', 'fullscreen', 'help',
    'image', 'imageManager', 'inlineClass', 'inlineStyle', 'lineBreaker', 'link', 'lists',
    'paragraphFormat', 'paragraphStyle', 'quickInsert', 'quote', 'table', 'url', 'video',
    'wordPaste', 'specialCharacters',
    'wiris'
  ],
  mathTypeParameters: {
    editorEnabled: true,
    chemEnabled: true,
    serviceProviderProperties: {
      URI: defaultWirisServiceProviderUri,
    },
  }
};

export const withFroalaLicense = (config = {}) => {
  const nextConfig = { ...config };

  // MathType/WIRIS needs a Service Provider URI so it can load its configuration (configurationjs)
  // and enable the MathType/ChemType buttons. CRA only exposes REACT_APP_* env vars, so we set a
  // sane default that works in dev and can be overridden via REACT_APP_WIRIS_SERVICE_PROVIDER_URI.
  if (!nextConfig.mathTypeParameters) {
    nextConfig.mathTypeParameters = {
      serviceProviderProperties: {
        URI: defaultWirisServiceProviderUri,
      },
    };
  } else if (
    !nextConfig.mathTypeParameters.serviceProviderProperties ||
    !nextConfig.mathTypeParameters.serviceProviderProperties.URI
  ) {
    nextConfig.mathTypeParameters = {
      ...nextConfig.mathTypeParameters,
      serviceProviderProperties: {
        ...(nextConfig.mathTypeParameters.serviceProviderProperties || {}),
        URI: defaultWirisServiceProviderUri,
      },
    };
  }

  // MathType/WIRIS outputs MathML and uses empty tags like <none/> and <mprescripts/>.
  // These defaults can be overridden by callers if needed.
  if (!nextConfig.htmlAllowedTags) {
    nextConfig.htmlAllowedTags = ['.*'];
  }

  if (!nextConfig.htmlAllowedAttrs) {
    nextConfig.htmlAllowedAttrs = ['.*'];
  }

  if (!nextConfig.htmlAllowedEmptyTags) {
    nextConfig.htmlAllowedEmptyTags = ['mprescripts', 'none'];
  } else {
    const emptyTags = Array.isArray(nextConfig.htmlAllowedEmptyTags)
      ? nextConfig.htmlAllowedEmptyTags
      : [];
    if (!emptyTags.includes('mprescripts')) emptyTags.push('mprescripts');
    if (!emptyTags.includes('none')) emptyTags.push('none');
    nextConfig.htmlAllowedEmptyTags = emptyTags;
  }

  if (froalaLicenseKey) {
    if (!nextConfig.licenseKey) {
      nextConfig.licenseKey = froalaLicenseKey;
    }

    if (!nextConfig.key) {
      nextConfig.key = froalaLicenseKey;
    }
  }

  if (defaultUploadUrl) {
    if (!nextConfig.imageUploadURL) {
      nextConfig.imageUploadURL = defaultUploadUrl;
    }

    if (typeof nextConfig.imageUpload === 'undefined') {
      nextConfig.imageUpload = true;
    }

    if (!nextConfig.imageUploadMethod) {
      nextConfig.imageUploadMethod = 'POST';
    }

    if (!nextConfig.imageUploadParam) {
      nextConfig.imageUploadParam = 'file';
    }

    if (!nextConfig.imageAllowedTypes) {
      nextConfig.imageAllowedTypes = ['jpeg', 'jpg', 'png', 'gif', 'svg', 'webp'];
    }

    if (!nextConfig.imageMaxSize) {
      nextConfig.imageMaxSize = defaultImageMaxSize;
    }
  }

  return nextConfig;
};

export default withFroalaLicense;
